import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import ContactSection from '../components/ContactSection';
import { appointmentsApi, type EasyAppointmentsService, type EasyAppointmentsProvider, type AvailabilitySlot, type BookingResponse } from '../services/appointmentsApi';
import '../styles/impressum-page.css'; // Verwende das gleiche Styling wie andere Seiten
import '../styles/booking-animations.css';
import '../styles/page-layout.css';

interface TimeSlot {
  id: string;
  time: string;
  date: string;
  available: boolean;
  start_datetime: string;
  end_datetime: string;
}

interface BookingData {
  selectedSlot: TimeSlot | null;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerMessage: string;
  service?: EasyAppointmentsService;
  provider?: EasyAppointmentsProvider;
}

const BeratungsterminPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'slots' | 'details' | 'confirmation'>('slots');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [bookingResponse, setBookingResponse] = useState<BookingResponse | null>(null);
  const [bookingData, setBookingData] = useState<BookingData>({
    selectedSlot: null,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerMessage: '',
    service: undefined,
    provider: undefined
  });

  // Initialize API data
  useEffect(() => {
    const initializeData = async () => {
      try {
        console.log('Initializing booking data...');
        
        const [servicesData, providersData] = await Promise.all([
          appointmentsApi.getServices(),
          appointmentsApi.getProviders()
        ]);
        
        console.log('Received data:', { servicesData, providersData });
        
        // Set default service and provider with explicit validation
        const defaultService = servicesData.length > 0 ? servicesData.find(s => s.id === '1') || servicesData[0] : {
          id: '1',
          name: 'Kostenlose Erstberatung',
          duration: 30,
          price: 0,
          description: '30 Minuten kostenlose Beratung für Ihr Digitalprojekt'
        };
        
        const defaultProvider = providersData.length > 0 ? providersData[0] : {
          id: '4', // Korrekte Provider-ID vom Server
          firstName: 'Henri',
          lastName: 'Stimm',
          email: 'henri@stimm.dev'
        };
        
        // Ensure IDs are strings and not null/undefined
        if (defaultService.id && defaultProvider.id) {
          setBookingData(prev => ({ 
            ...prev, 
            service: defaultService,
            provider: defaultProvider
          }));
          console.log('Set default booking data:', { defaultService, defaultProvider });
        } else {
          console.error('Invalid service or provider IDs:', { defaultService, defaultProvider });
        }
        
        console.log('API configured:', appointmentsApi.isApiConfigured());
        if (!appointmentsApi.isApiConfigured()) {
          console.warn('EasyAppointments API nicht konfiguriert.');
        }
      } catch (err) {
        console.error('Failed to initialize booking data:', err);
        setError('Fehler beim Laden der Buchungsdaten');
        
        // Set fallback data even if API fails
        setBookingData(prev => ({ 
          ...prev, 
          service: {
            id: '1',
            name: 'Kostenlose Erstberatung',
            duration: 30,
            price: 0,
            description: '30 Minuten kostenlose Beratung für Ihr Digitalprojekt'
          },
          provider: {
            id: '4', // Korrekte Provider-ID als Fallback
            firstName: 'Henri',
            lastName: 'Stimm',
            email: 'henri@stimm.dev'
          }
        }));
      }
    };
    
    initializeData();
  }, []);

  // Generate available dates for the next 30 days (excluding weekends)
  const availableDates = useMemo(() => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Skip weekends for demo
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    return dates;
  }, []);

  // Convert API slots to our TimeSlot format
  const convertApiSlotsToTimeSlots = (apiSlots: AvailabilitySlot[], date: string): TimeSlot[] => {
    return apiSlots.map((slot, index) => {
      const startTime = new Date(slot.start);
      
      return {
        id: `${date}-${index}`,
        time: startTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        date,
        available: true,
        start_datetime: slot.start,
        end_datetime: slot.end
      };
    });
  };

  // Load slots when date changes
  useEffect(() => {
    const loadSlots = async () => {
      if (selectedDate && bookingData.service?.id && bookingData.provider?.id) {
        setIsLoading(true);
        setError('');
        
        try {
          console.log('Loading slots for:', {
            serviceId: bookingData.service.id,
            providerId: bookingData.provider.id,
            date: selectedDate,
            serviceName: bookingData.service.name,
            providerName: `${bookingData.provider.firstName} ${bookingData.provider.lastName}`
          });
          
          // Double check that IDs are valid before making API call
          if (!bookingData.service.id || !bookingData.provider.id) {
            console.warn('Missing service or provider ID, using demo data');
            const demoSlots = generateDemoTimeSlots(selectedDate);
            setAvailableSlots(demoSlots);
            return;
          }
          
          const apiSlots = await appointmentsApi.getAvailabilities(
            bookingData.service.id,
            bookingData.provider.id,
            selectedDate
          );
          
          console.log('API returned slots:', apiSlots);
          
          if (apiSlots.length === 0) {
            console.log('No slots returned from API, using demo data');
            const demoSlots = generateDemoTimeSlots(selectedDate);
            setAvailableSlots(demoSlots);
          } else {
            const timeSlots = convertApiSlotsToTimeSlots(apiSlots, selectedDate);
            setAvailableSlots(timeSlots);
          }
        } catch (err) {
          console.error('Failed to load time slots:', err);
          setError('Fehler beim Laden der verfügbaren Termine');
          
          // Fallback to demo data
          const demoSlots = generateDemoTimeSlots(selectedDate);
          setAvailableSlots(demoSlots);
        } finally {
          setIsLoading(false);
        }
      } else {
        console.log('Missing required data for loading slots:', {
          selectedDate,
          serviceId: bookingData.service?.id,
          providerId: bookingData.provider?.id
        });
      }
    };
    
    loadSlots();
  }, [selectedDate, bookingData.service, bookingData.provider]);

  // Generate demo time slots as fallback
  const generateDemoTimeSlots = (date: string): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    const times = [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
    ];

    times.forEach((time) => {
      // Randomly make some slots unavailable for demo
      const available = Math.random() > 0.3;
      const [hours, minutes] = time.split(':');
      const startDateTime = new Date(`${date}T${hours}:${minutes}:00`);
      const endDateTime = new Date(startDateTime);
      endDateTime.setMinutes(startDateTime.getMinutes() + 30);
      
      slots.push({
        id: `${date}-${time}`,
        time,
        date,
        available,
        start_datetime: startDateTime.toISOString(),
        end_datetime: endDateTime.toISOString()
      });
    });

    return slots;
  };

  // Set initial date to tomorrow
  useEffect(() => {
    if (availableDates.length > 0) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates]);

  const handleSlotSelect = (slot: TimeSlot) => {
    if (!slot.available) return;
    
    setBookingData(prev => ({ ...prev, selectedSlot: slot }));
    
    // Animate step transition
    setTimeout(() => {
      setCurrentStep('details');
    }, 100);
  };

  const handleInputChange = (field: keyof BookingData, value: string) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!bookingData.selectedSlot || !bookingData.service || !bookingData.provider) {
      setError('Fehlende Buchungsdaten');
      return;
    }
    
    setIsLoading(true);
    setError('');

    try {
      const [firstName, ...lastNameParts] = bookingData.customerName.trim().split(' ');
      const lastName = lastNameParts.join(' ') || '';
      
      const bookingRequest = {
        service_id: bookingData.service.id,
        provider_id: bookingData.provider.id,
        start_datetime: bookingData.selectedSlot.start_datetime,
        end_datetime: bookingData.selectedSlot.end_datetime,
        customer: {
          first_name: firstName,
          last_name: lastName,
          email: bookingData.customerEmail,
          phone: bookingData.customerPhone,
        },
        notes: bookingData.customerMessage || undefined,
      };
      
      console.log('Submitting booking request:', bookingRequest);
      
      const response = await appointmentsApi.bookAppointment(bookingRequest);
      
      console.log('Booking response received:', response);
      setBookingResponse(response);
      
      // Show success message briefly before transitioning
      setTimeout(() => {
        setCurrentStep('confirmation');
      }, 500);
      
    } catch (err) {
      console.error('Booking failed:', err);
      
      // Show detailed error message
      let errorMessage = 'Die Buchung konnte nicht abgeschlossen werden.';
      
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateShort = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit'
    });
  };

  return (
    <Layout
      title="Kostenlose Erstberatung buchen | Stimm.DEV"
      description="Buchen Sie einen kostenlosen Beratungstermin für Ihr App- oder Website-Projekt. Professionelle Beratung ohne Verpflichtungen."
      keywords="Beratungstermin, kostenlose Beratung, App-Entwicklung, Website-Entwicklung, Stimm.DEV"
    >
      <section className="wrapper impressum-wrapper">
        <div className="page-container">
          {/* Header */}
          <div className="page-header">
            <span className="section-title">Kostenlos & Unverbindlich</span>
            <h1>
              Erstberatung buchen
            </h1>
            <p>
              {bookingData.service?.description || '30 Minuten persönliche Beratung für Ihr Digitalprojekt'}
            </p>
            
            {/* Debug Info (nur in Entwicklung) */}
          </div>

          {/* Main Content */}
          <div className="booking-card">
            
            {/* Progress Indicator */}
            <div className="progress-indicator">
              <div style={{
                background: currentStep === 'slots' ? 'rgba(0, 158, 209, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: currentStep === 'slots' ? '2px solid #009ed1' : '2px solid rgba(255, 255, 255, 0.2)',
                color: currentStep === 'slots' ? '#009ed1' : 'rgba(255, 255, 255, 0.7)'
              }} className={`progress-step ${currentStep === 'slots' ? 'active' : ''}`}>
                <span style={{ 
                  background: currentStep === 'slots' ? '#009ed1' : 'rgba(255, 255, 255, 0.3)',
                  color: currentStep === 'slots' ? 'white' : 'rgba(255, 255, 255, 0.7)'
                }} className="progress-step-number">1</span>
                <span className="progress-step-text">Termin</span>
              </div>
              
              <div className="progress-connector"></div>
              
              <div style={{
                background: currentStep === 'details' ? 'rgba(0, 158, 209, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: currentStep === 'details' ? '2px solid #009ed1' : '2px solid rgba(255, 255, 255, 0.2)',
                color: currentStep === 'details' ? '#009ed1' : 'rgba(255, 255, 255, 0.7)'
              }} className={`progress-step ${currentStep === 'details' ? 'active' : currentStep === 'confirmation' ? 'completed' : ''}`}>
                <span style={{ 
                  background: currentStep === 'details' ? '#009ed1' : 'rgba(255, 255, 255, 0.3)',
                  color: currentStep === 'details' ? 'white' : 'rgba(255, 255, 255, 0.7)'
                }} className="progress-step-number">2</span>
                <span className="progress-step-text">Kontaktdaten</span>
              </div>
              
              <div className="progress-connector"></div>
              
              <div style={{
                background: currentStep === 'confirmation' ? 'rgba(0, 158, 209, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                border: currentStep === 'confirmation' ? '2px solid #009ed1' : '2px solid rgba(255, 255, 255, 0.2)',
                color: currentStep === 'confirmation' ? '#009ed1' : 'rgba(255, 255, 255, 0.7)'
              }} className={`progress-step ${currentStep === 'confirmation' ? 'active' : ''}`}>
                <span style={{ 
                  background: currentStep === 'confirmation' ? '#009ed1' : 'rgba(255, 255, 255, 0.3)',
                  color: currentStep === 'confirmation' ? 'white' : 'rgba(255, 255, 255, 0.7)'
                }} className="progress-step-number">3</span>
                <span className="progress-step-text">Bestätigung</span>
              </div>
            </div>

            {error && (
              <div style={{
                background: 'rgba(220, 53, 69, 0.2)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '2rem',
                color: '#ffffff',
                textAlign: 'center'
              }} className="error-message">
                <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
                {error}
              </div>
            )}

            {/* Step 1: Time Slot Selection */}
            {currentStep === 'slots' && (
              <div className="booking-step-transition">
                <h2 style={{ color: '#009ed1', marginTop: '0', marginBottom: '2rem', fontSize: '1.8rem' }}>
                  Wählen Sie Ihren Wunschtermin
                </h2>
                
                <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '3rem', lineHeight: '1.6' }}>
                  Alle Termine sind kostenlos und unverbindlich. Die Beratung erfolgt per Video-Call oder Telefon.
                </p>

                {/* Date Selection */}
                <div style={{ marginBottom: '3rem' }}>
                  <h3 style={{ color: '#009ed1', marginBottom: '1.5rem', fontSize: '1.3rem' }}>
                    Verfügbare Termine
                  </h3>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', 
                    gap: '1rem', 
                    marginBottom: '2rem'
                  }} className="calendar-grid">
                    {availableDates.slice(0, 7).map(date => (
                      <button
                        key={date}
                        className="date-button"
                        style={{
                          background: selectedDate === date ? '#009ed1' : 'rgba(0, 158, 209, 0.1)',
                          border: selectedDate === date ? '2px solid #009ed1' : '2px solid rgba(0, 158, 209, 0.2)',
                          borderRadius: '0.75rem',
                          padding: '1rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          color: selectedDate === date ? 'white' : 'rgba(255, 255, 255, 0.9)',
                          textAlign: 'center',
                          fontSize: '0.9rem'
                        }}
                        onClick={() => setSelectedDate(date)}
                      >
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                          {formatDateShort(date)}
                        </div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                          {new Date(date).toLocaleDateString('de-DE', { weekday: 'short' })}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div>
                    <h3 style={{ color: '#009ed1', marginBottom: '1.5rem', fontSize: '1.3rem' }}>
                      {formatDate(selectedDate)}
                    </h3>
                    
                    {isLoading ? (
                      <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <LoadingSpinner size="small" />
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '1rem' }}>
                          Lade verfügbare Zeiten...
                        </p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '3rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '0.75rem',
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <i className="fas fa-calendar-times" style={{ fontSize: '3rem', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '1rem' }}></i>
                        <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '0' }}>
                          Keine verfügbaren Termine an diesem Tag.
                        </p>
                      </div>
                    ) : (
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                        gap: '1rem'
                      }} className="time-slots-grid">
                        {availableSlots.map(slot => (
                          <button
                            key={slot.id}
                            disabled={!slot.available}
                            className="time-slot"
                            style={{
                              background: slot.available ? 'rgba(0, 158, 209, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                              border: slot.available ? '2px solid rgba(0, 158, 209, 0.3)' : '2px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '0.5rem',
                              padding: '1rem',
                              cursor: slot.available ? 'pointer' : 'not-allowed',
                              transition: 'all 0.3s ease',
                              color: slot.available ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                              textAlign: 'center',
                              opacity: slot.available ? 1 : 0.5,
                              position: 'relative'
                            }}
                            onClick={() => handleSlotSelect(slot)}
                          >
                            <div style={{ fontWeight: '600' }}>{slot.time}</div>
                            {!slot.available && (
                              <div style={{ fontSize: '0.7rem', marginTop: '0.25rem' }}>Belegt</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Customer Details */}
            {currentStep === 'details' && bookingData.selectedSlot && (
              <div className="booking-step-transition">
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem' }}>
                  <button 
                    onClick={() => setCurrentStep('slots')}
                    className="booking-button"
                    style={{
                      background: 'transparent',
                      border: '2px solid #009ed1',
                      borderRadius: '0.5rem',
                      color: '#009ed1',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      marginRight: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="fas fa-arrow-left" style={{ marginRight: '0.5rem' }}></i>
                    Zurück
                  </button>
                  <h2 style={{ color: '#009ed1', margin: '0', fontSize: '1.8rem' }}>
                    Ihre Kontaktdaten
                  </h2>
                </div>

                {/* Booking Summary */}
                <div style={{ 
                  background: 'rgba(0, 158, 209, 0.1)', 
                  padding: '2rem', 
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(0, 158, 209, 0.2)',
                  marginBottom: '3rem'
                }} className="booking-summary">
                  <h3 style={{ color: '#009ed1', marginBottom: '1rem', fontSize: '1.2rem' }}>
                    Gewählter Termin
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem'
                  }}>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Service:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.service?.name || 'Kostenlose Erstberatung'}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Datum:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {formatDate(bookingData.selectedSlot.date)}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Uhrzeit:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.selectedSlot.time} Uhr
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Dauer:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.service?.duration || 30} Minuten
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Kosten:</strong>
                      <div style={{ color: '#28a745', fontWeight: '600' }}>
                        {bookingData.service?.price === 0 ? 'Kostenlos' : `${bookingData.service?.price || 0}€`}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Berater:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.provider ? `${bookingData.provider.firstName} ${bookingData.provider.lastName}` : 'Henri Stimm'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer Form */}
                <form onSubmit={handleSubmit}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <div className="form-field">
                      <label style={{ 
                        display: 'block', 
                        color: '#ffffff', 
                        marginBottom: '0.5rem', 
                        fontWeight: '600' 
                      }}>
                        Vollständiger Name *
                      </label>
                      <input
                        type="text"
                        value={bookingData.customerName}
                        onChange={(e) => handleInputChange('customerName', e.target.value)}
                        required
                        placeholder="Max Mustermann"
                        style={{
                          width: '100%',
                          padding: '0.8rem',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>

                    <div className="form-field">
                      <label style={{ 
                        display: 'block', 
                        color: '#ffffff', 
                        marginBottom: '0.5rem', 
                        fontWeight: '600' 
                      }}>
                        E-Mail-Adresse *
                      </label>
                      <input
                        type="email"
                        value={bookingData.customerEmail}
                        onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                        required
                        placeholder="max@beispiel.de"
                        style={{
                          width: '100%',
                          padding: '0.8rem',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }} className="form-field">
                      <label style={{ 
                        display: 'block', 
                        color: '#ffffff', 
                        marginBottom: '0.5rem', 
                        fontWeight: '600' 
                      }}>
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        value={bookingData.customerPhone}
                        onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                        required
                        placeholder="+49 30 12345678"
                        style={{
                          width: '100%',
                          padding: '0.8rem',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          fontSize: '1rem',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }} className="form-field">
                      <label style={{ 
                        display: 'block', 
                        color: '#ffffff', 
                        marginBottom: '0.5rem', 
                        fontWeight: '600' 
                      }}>
                        Nachricht (optional)
                      </label>
                      <textarea
                        value={bookingData.customerMessage}
                        onChange={(e) => handleInputChange('customerMessage', e.target.value)}
                        placeholder="Beschreiben Sie kurz Ihr Projekt oder besondere Wünsche für das Beratungsgespräch..."
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.8rem',
                          border: '2px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          fontSize: '1rem',
                          resize: 'vertical',
                          transition: 'all 0.3s ease'
                        }}
                      />
                    </div>
                  </div>

                  {/* Info Box */}
                  <div style={{ 
                    background: 'rgba(40, 167, 69, 0.1)', 
                    border: '1px solid rgba(40, 167, 69, 0.3)',
                    borderRadius: '0.75rem',
                    padding: '1.5rem',
                    marginBottom: '2rem'
                  }}>
                    <h4 style={{ color: '#28a745', marginBottom: '1rem', fontSize: '1.1rem' }}>
                      Was erwartet Sie:
                    </h4>
                    <ul style={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.6', paddingLeft: '1.5rem', margin: '0' }}>
                      <li>Persönliches Gespräch per Video-Call oder Telefon</li>
                      <li>Analyse Ihrer Projektanforderungen</li>
                      <li>Kostenlose Ersteinschätzung und Empfehlungen</li>
                      <li>Unverbindliches Angebot bei Interesse</li>
                    </ul>
                  </div>

                  <button 
                    type="submit"
                    disabled={isLoading || !bookingData.customerName || !bookingData.customerEmail || !bookingData.customerPhone}
                    className="booking-button"
                    style={{
                      background: (!bookingData.customerName || !bookingData.customerEmail || !bookingData.customerPhone) ? 'rgba(0, 158, 209, 0.5)' : '#009ed1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '1rem 2rem',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      cursor: (!bookingData.customerName || !bookingData.customerEmail || !bookingData.customerPhone) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      width: '100%',
                      position: 'relative'
                    }}
                  >
                    {isLoading && <div className="loading-overlay"><LoadingSpinner size="small" /></div>}
                    {isLoading ? (
                      <span style={{ opacity: 0.7 }}>Wird gebucht...</span>
                    ) : (
                      <>
                        <i className="fas fa-check" style={{ marginRight: '0.5rem' }}></i>
                        Termin buchen
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {currentStep === 'confirmation' && bookingData.selectedSlot && (
              <div className="booking-step-transition">
                <div style={{ textAlign: 'center' }} className="confirmation-success">
                  <div style={{ 
                    fontSize: '3rem', 
                    color: 'rgba(0, 158, 209, 0.8)', 
                    marginBottom: '2rem',
                    background: 'rgba(0, 158, 209, 0.05)',
                    borderRadius: '50%',
                    width: '80px',
                    height: '80px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 2rem auto',
                    border: '1px solid rgba(0, 158, 209, 0.3)'
                  }}>
                    <i className="fas fa-check" style={{ fontSize: '2rem' }}></i>
                  </div>
                  
                  <h2 style={{ color: '#009ed1', margin: '0 0 1rem 0', fontSize: '2.5rem', fontWeight: '700' }}>
                    Termin erfolgreich gebucht!
                  </h2>
                  
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '1.2rem', marginBottom: '3rem' }}>
                    Wir haben Ihnen eine Bestätigung per E-Mail gesendet.
                  </p>
                </div>

                {/* Booking Details */}
                <div style={{ 
                  background: 'rgba(0, 158, 209, 0.1)', 
                  padding: '2rem', 
                  borderRadius: '0.75rem',
                  border: '1px solid rgba(0, 158, 209, 0.2)',
                  marginBottom: '3rem',
                  textAlign: 'left'
                }} className="booking-summary">
                  <h3 style={{ color: '#009ed1', marginBottom: '1.5rem', fontSize: '1.3rem' }}>
                    Ihre Termindetails
                  </h3>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '1rem'
                  }}>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Termin:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingResponse?.service?.name || bookingData.service?.name || 'Kostenlose Erstberatung'}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Datum:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {formatDate(bookingData.selectedSlot.date)}
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Uhrzeit:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.selectedSlot.time} Uhr
                      </div>
                    </div>
                    <div>
                      <strong style={{ color: '#ffffff' }}>Kontakt:</strong>
                      <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {bookingData.customerEmail}
                      </div>
                    </div>
                    {bookingResponse && (
                      <>
                        <div>
                          <strong style={{ color: '#ffffff' }}>Buchungs-ID:</strong>
                          <div style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: 'monospace' }}>
                            {bookingResponse.id}
                          </div>
                        </div>
                        {bookingResponse.provider && (
                          <div>
                            <strong style={{ color: '#ffffff' }}>Berater:</strong>
                            <div style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {bookingResponse.provider.first_name} {bookingResponse.provider.last_name}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Next Steps */}
                <div style={{ 
                  background: 'rgba(0, 158, 209, 0.1)', 
                  border: '1px solid rgba(0, 158, 209, 0.3)',
                  borderRadius: '0.75rem',
                  padding: '2rem',
                  marginBottom: '3rem',
                  textAlign: 'left'
                }} className="next-steps">
                  <h3 style={{ color: '#009ed1', marginBottom: '1.5rem', fontSize: '1.3rem' }}>
                    Was passiert als nächstes?
                  </h3>
                  <div style={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: '1.8' }}>
                    <p style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <i className="fas fa-envelope" style={{ color: '#009ed1', marginRight: '1rem', marginTop: '0.2rem', fontSize: '1.1rem' }}></i>
                      <span><strong>E-Mail-Bestätigung:</strong> Sie erhalten in Kürze eine E-Mail mit allen Termindetails und dem Zugangslink.</span>
                    </p>
                    <p style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <i className="fas fa-calendar-plus" style={{ color: '#009ed1', marginRight: '1rem', marginTop: '0.2rem', fontSize: '1.1rem' }}></i>
                      <span><strong>Kalender-Eintrag:</strong> Fügen Sie den Termin zu Ihrem Kalender hinzu (Link in der E-Mail).</span>
                    </p>
                    <p style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <i className="fas fa-bell" style={{ color: '#009ed1', marginRight: '1rem', marginTop: '0.2rem', fontSize: '1.1rem' }}></i>
                      <span><strong>Erinnerung:</strong> Wir senden Ihnen 24 Stunden vor dem Termin eine Erinnerung.</span>
                    </p>
                    <p style={{ display: 'flex', alignItems: 'flex-start', margin: '0' }}>
                      <i className="fas fa-clipboard-list" style={{ color: '#009ed1', marginRight: '1rem', marginTop: '0.2rem', fontSize: '1.1rem' }}></i>
                      <span><strong>Vorbereitung:</strong> Bereiten Sie gerne Fragen zu Ihrem Projekt vor.</span>
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ 
                  display: 'flex', 
                  gap: '1rem', 
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <button 
                    onClick={() => window.location.href = '/'}
                    className="booking-button"
                    style={{
                      background: '#009ed1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '1rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="fas fa-home" style={{ marginRight: '0.5rem' }}></i>
                    Zur Startseite
                  </button>
                  
                  <button 
                    onClick={() => {
                      setCurrentStep('slots');
                      setBookingData({
                        selectedSlot: null,
                        customerName: '',
                        customerEmail: '',
                        customerPhone: '',
                        customerMessage: '',
                        service: bookingData.service,
                        provider: bookingData.provider
                      });
                      setBookingResponse(null);
                    }}
                    className="booking-button"
                    style={{
                      background: 'transparent',
                      color: '#009ed1',
                      border: '2px solid #009ed1',
                      borderRadius: '0.5rem',
                      padding: '1rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <i className="fas fa-plus" style={{ marginRight: '0.5rem' }}></i>
                    Weiteren Termin buchen
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection />
    </Layout>
  );
};

export default BeratungsterminPage;
